/**
 * The CSV reading the admin portal's uploads share: auto-enroll's roster and the alumni console's
 * client import.
 *
 * Hand-rolled rather than pulling in a parser: the grammar we need is one sheet
 * exported from Excel or Google Sheets — quoted fields, doubled quotes, either
 * line ending. That's a few lines, and a dependency in the admin bundle needs
 * more justification than that.
 */

/**
 * Splits CSV text into cells.
 *
 * Tracks quoting rather than splitting on commas, because a name like
 * `"Smith, Jr"` is exactly what breaks the naive version.
 */
export const parseCsv = (text: string): string[][] => {
    const rows: string[][] = [];
    let row: string[] = [];
    let cell = "";
    let quoted = false;

    // Strip a UTF-8 BOM — Excel writes one, and it would otherwise glue itself to
    // the first header name and defeat the column matching.
    const input = text.replace(/^﻿/, "");

    for (let i = 0; i < input.length; i += 1) {
        const char = input[i];

        if (quoted) {
            if (char === '"') {
                // A doubled quote inside a quoted field is one literal quote.
                if (input[i + 1] === '"') {
                    cell += '"';
                    i += 1;
                } else {
                    quoted = false;
                }
            } else {
                cell += char;
            }
            continue;
        }

        if (char === '"') {
            quoted = true;
        } else if (char === ",") {
            row.push(cell);
            cell = "";
        } else if (char === "\n" || char === "\r") {
            // Treat CRLF as one break, not two.
            if (char === "\r" && input[i + 1] === "\n") i += 1;
            row.push(cell);
            rows.push(row);
            row = [];
            cell = "";
        } else {
            cell += char;
        }
    }

    // Whatever is buffered when the text ends is the last cell of the last row.
    if (cell !== "" || row.length > 0) {
        row.push(cell);
        rows.push(row);
    }

    // A blank line is not a row.
    return rows.filter((cells) => cells.some((value) => value.trim() !== ""));
};

/** A header as compared: trimmed, lowercase, underscores as spaces, single-spaced. */
export const normalizeHeader = (value: string): string =>
    value.trim().toLowerCase().replace(/_/g, " ").replace(/\s+/g, " ");

const withoutSpaces = (value: string): string => value.replace(/\s/g, "");

/**
 * The index of the first header any of `accepted` names, or -1. Spaces are ignored on both sides, so
 * "first name" also matches `FirstName` and `first_name` without listing every run-together spelling.
 */
export const findColumn = (headers: string[], accepted: readonly string[]): number => {
    const names = new Set<string>(accepted.map((name) => withoutSpaces(normalizeHeader(name))));
    return headers.findIndex((header) => names.has(withoutSpaces(normalizeHeader(header))));
};
